import clsx from 'clsx';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { App, Badge, Button, Divider, Grid, Spin, Tooltip } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useTranslation } from 'react-i18next';
import packageJson from '../../../package.json';
import IndeterminateProgress from '../../components/Common/IndeterminateProgress';
import ActivateTotpOverlay from '../../components/Common/ActivateTotpOverlay';
import AppUpdatedOverlay from '../../components/Common/AppUpdatedOverlay';
import SessionExpiredOverlay from '../../components/Common/SessionExpiredOverlay';
import ShortcutHint from '../../components/Common/ShortcutHint';
import TagsList from '../../components/Common/TagsList';
import TooManyRequestsOverlay from '../../components/Common/TooManyRequestsOverlay';
import TransferProgressDrawers from '../../components/Common/TransferProgressDrawers';
import FilegrantViewerDrawer from '../../components/Viewer/FilegrantViewerDrawer';
import ErrorBoundary from '../../components/Core/ErrorBoundary';
import Header from '../../components/Core/Header';
import { Icon } from '../../components/Core/Icon';
import MainButtonMenu from '../../components/Core/MainButtonMenu';
import KeyboardShortcutEngineHost from '../../components/Core/KeyboardShortcutEngineHost/KeyboardShortcutEngineHost';
import RightPanel from '../../components/Core/RightPanel';
import Sidebar from '../../components/Core/Sidebar';
import { KeyboardShortcutsProvider } from '../../contexts/KeyboardShortcutsContext';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { InterfaceLimitationEnum } from '../../enums/InterfaceLimitationEnum';
import { useAuth } from '../../hooks/useAuth';
import { useBranding } from '../../hooks/useBranding';
import { useFilePasswordAccess } from '../../hooks/useFilePasswordAccess';
import { useOnBehalfOf } from '../../hooks/useOnBehalfOf';
import { usePermissions } from '../../hooks/usePermissions';
import { useRightPanel } from '../../hooks/useRightPanel';
import { useAppStore } from '../../store/store';
import { filteredEmailRouterPages } from '../../utils/emailRouterPages';
import { openKeyboardShortcutsFormModal } from '../../utils/openKeyboardShortcutsFormModal';
import { filteredRouterPages } from '../../utils/routerPages';
import { hasInterfaceLimitation } from '../../utils/utils';

function MainLayout() {
  const location = useLocation();
  const { receivedFilesCount } = usePermissions();
  const { authenticatedUser, redirectLoggedInUser } = useAuth();
  const { isOnBehalfOfUser } = useOnBehalfOf();
  const {
    rightPanelContent,
    isRightPanelOpen,
    closeRightPanel,
    hasRightPanelCloseButton,
    isRightPanelCheckDirtyOnClose,
  } = useRightPanel();
  const isViewerDrawerOpen = useAppStore((s) => s.isViewerDrawerOpen);
  // Drawer owns Details/AI as nested split — keep global panel closed to avoid dual layout fight.
  const showGlobalRightPanel = isRightPanelOpen && !isViewerDrawerOpen;
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  const isGlobalLoaderVisible = useAppStore((s) => s.isGlobalLoaderVisible);
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const screens = Grid.useBreakpoint();
  const { appEvents } = useAppStore();
  const openEmailComposer = useAppStore((s) => s.openEmailComposer);
  const emailUnreadCount = useAppStore((s) => s.emailUnreadCount);
  const fetchEmailUnreadCount = useAppStore((s) => s.fetchEmailUnreadCount);
  const { brandingData } = useBranding();
  const isEmailEnabled =
    import.meta.env.VITE_APP_EMAIL_ENABLED === 'true' && authenticatedUser?.isEmailGrantEnabled;
  const isEmailMode = isEmailEnabled && location.pathname.startsWith('/emails');
  const emailUnreadBadgeCount = !isEmailMode && emailUnreadCount > 0 ? emailUnreadCount : 0;
  const sectionSegmentSelectedClassName =
    'bg-white !no-underline text-blue-dark shadow-sm dark:!text-black [&_.ant-badge]:!text-inherit [&_span]:!text-inherit [&_svg]:text-blue-dark dark:[&_svg]:!text-black';
  const sectionSegmentUnselectedClassName =
    '!no-underline !text-gray-dark hover:!text-blue-dark dark:hover:!text-black [&_svg]:text-current';
  const routerPageContext = useMemo(
    () => ({
      authenticatedUser,
    }),
    [authenticatedUser],
  );

  const handleOpenKeyboardShortcuts = useCallback(() => {
    openKeyboardShortcutsFormModal({ modal, t });
  }, [modal, t]);

  const shortcutsLabel = t('shortcuts.title');
  const appVersionLabel = `v${packageJson.version}`;
  const buildIdLabel = __BUILD_ID__.length > 10 ? __BUILD_ID__.slice(0, 7) : __BUILD_ID__;
  // ponytail: antd lg (992) ≈ Tailwind sider expand (1024); fine for icon-only tooltip
  const isIconOnlySider = !screens.lg;

  // Match sidebar NavLink hover (hover:bg-black/5). Ant Design type="text" hover bg is only
  // colorFillTertiary (~4% black) — effectively invisible on sidebar bg-background. cursor-pointer!
  // beats any leftover UA/default cursor if antd hash styles fail to attach.
  const shortcutsButton = (
    <Button
      type="text"
      block
      aria-label={shortcutsLabel}
      onClick={handleOpenKeyboardShortcuts}
      className={clsx(
        'text-blue-dark/70 h-auto! cursor-pointer! justify-center gap-1.5 px-1! py-1.5!',
        'rounded-md! transition-all duration-400 hover:bg-black/5!',
        'lg:justify-start lg:gap-2 lg:px-2!',
      )}
    >
      <Icon name="KeyboardIcon" className="h-5 w-5 shrink-0" />
      <span className="hidden min-w-0 flex-1 truncate text-left text-xs lg:block">
        {shortcutsLabel}
      </span>
      <ShortcutHint
        shortcutId="app.openShortcutsHelp"
        className="text-gray-dark hidden shrink-0 text-[10px] whitespace-nowrap lg:inline"
      />
    </Button>
  );

  /**
   * Memoized array of updated router pages after applying filters and transformations.
   *
   * The `updatedRouterPages` variable is derived from the `routerPages` array by performing the following operations:
   * - Filters out pages where the `isHidden` property is set to `true`.
   * - Adds or modifies specific properties for certain pages based on their `key` value:
   *   - For pages with the `key` of 'shared-with-me', it adds `notificationsCount` using `receivedFilesCount`
   *   - For pages with the `key` of 'settings', it only sets `isDisabled`.
   *
   * Dependencies for memoization include:
   * - `receivedFilesCount`
   */
  const updatedRouterPages = useMemo(() => {
    const filePages = filteredRouterPages(routerPageContext)
      .filter((page) => !page.isHidden)
      .map((page) => {
        if (page.key === 'received') {
          return {
            ...page,
            notificationsCount: receivedFilesCount,
          };
        }

        if (page.key === 'settings') {
          return {
            ...page,
          };
        }
        return page;
      });

    if (isEmailMode) {
      // In email mode: show email pages + admin pages (users, groups, activity-logs, quarantine, dashboard, sales-dashboard, settings)
      const adminKeys = [
        'users',
        'groups',
        'activity-log',
        'antivirus-quarantine',
        'dashboard',
        'sales-dashboard',
        'settings',
      ];
      const adminPages = filePages.filter((p) => adminKeys.includes(p.key));
      const emailPages = filteredEmailRouterPages(routerPageContext).map((page) => {
        if (page.key === 'email-inbox') {
          return { ...page, notificationsCount: emailUnreadCount };
        }
        return page;
      });
      return [...emailPages, ...adminPages];
    }

    return filePages;
  }, [receivedFilesCount, isEmailMode, routerPageContext, emailUnreadCount]);

  /**
   * When the user lands exactly on /files (no section), redirect to the default files section for their role.
   */
  useEffect(() => {
    if (isEmailEnabled) {
      fetchEmailUnreadCount();
    }
  }, [isEmailEnabled, fetchEmailUnreadCount]);

  useEffect(() => {
    if (location.pathname === '/files') {
      redirectLoggedInUser();
    }
  }, [location.pathname, redirectLoggedInUser]);

  /**
   * @description Close the right panel on location change.
   */
  useEffect(() => {
    if (location.pathname !== prevPathname && isRightPanelOpen) {
      closeRightPanel();
    }

    setPrevPathname(location.pathname);
  }, [location.pathname, isRightPanelOpen, closeRightPanel, prevPathname]);

  return (
    <KeyboardShortcutsProvider>
      <KeyboardShortcutEngineHost />
      <div
        className={clsx(
          'bg-background flex min-h-full w-full grow overflow-hidden',
          isOnBehalfOfUser && 'relative',
        )}
      >
        <a
          href="#main-content"
          className="focus:text-blue-dark sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:shadow-lg"
        >
          {t('a11y.skipToMain')}
        </a>
        <AppUpdatedOverlay />
        <SessionExpiredOverlay />
        <TooManyRequestsOverlay />
        <ActivateTotpOverlay />
        {useFilePasswordAccess()}
        <Header />

        <IndeterminateProgress
          className={clsx(
            /* dashboard-no-print: indeterminate keyframes stretch past viewport and inflate print page width. */
            'dashboard-no-print absolute top-[var(--app-header-stack-height,3.5rem)] left-0 z-999 w-full p-0 transition-all duration-300',
            isGlobalLoaderVisible ? 'opacity-100' : 'opacity-0',
          )}
        />

        <Sidebar
          fixedHeader={
            <>
              {isEmailEnabled && (
                <nav
                  aria-label={t('a11y.sectionSelector')}
                  className="mx-auto mb-4 w-full px-1 lg:px-2"
                >
                  <div
                    className={clsx(
                      'relative flex h-full w-full flex-col items-center gap-1 rounded-xl p-1 font-medium lg:inline-grid lg:grid-cols-2 lg:rounded-full',
                      'border border-black/[0.06] bg-black/10',
                      'dark:border-gray-medium dark:bg-gray',
                    )}
                  >
                    {/* Sliding selected pill — desktop only */}
                    <span
                      aria-hidden="true"
                      className={clsx(
                        'absolute top-1 bottom-1 hidden w-[calc(50%-4px)] rounded-full lg:block',
                        'bg-white',
                        'shadow-sm',
                        'transition-transform duration-300 ease-[cubic-bezier(.2,.8,.2,1)]',
                        isEmailMode ? 'translate-x-[calc(100%+4px)]' : 'translate-x-1',
                      )}
                    />

                    <Link
                      to={`/files/${FilesTableEnum.Personal}`}
                      aria-current={!isEmailMode ? 'page' : undefined}
                      className={clsx(
                        'relative z-10 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg whitespace-nowrap lg:rounded-full',
                        'px-1.5 text-sm lg:px-2',
                        'transition-[color,background-color,transform] duration-200',
                        'focus-visible:ring-2 focus-visible:ring-[var(--ant-colorPrimary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ant-colorFillSecondary)] focus-visible:outline-none',
                        'motion-safe:active:scale-[0.98]',
                        !isEmailMode
                          ? sectionSegmentSelectedClassName
                          : sectionSegmentUnselectedClassName,
                      )}
                    >
                      <Icon name="FolderIcon" className="h-4 w-4 shrink-0" />
                      <span className="hidden whitespace-nowrap lg:inline">
                        {t('common.files')}
                      </span>
                      <span className="sr-only">{t('a11y.openFilesSection')}</span>
                    </Link>

                    <Link
                      to="/emails/inbox"
                      aria-current={isEmailMode ? 'page' : undefined}
                      className={clsx(
                        'relative z-10 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg font-semibold whitespace-nowrap lg:rounded-full',
                        'px-1.5 text-sm lg:px-2',
                        'transition-[color,background-color,transform] duration-200',
                        'focus-visible:ring-2 focus-visible:ring-[var(--ant-colorPrimary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ant-colorFillSecondary)] focus-visible:outline-none',
                        'motion-safe:active:scale-[0.98]',
                        isEmailMode
                          ? sectionSegmentSelectedClassName
                          : sectionSegmentUnselectedClassName,
                      )}
                    >
                      <Badge
                        count={emailUnreadBadgeCount}
                        overflowCount={99}
                        size="small"
                        className="text-inherit"
                        styles={{
                          // .ant-badge defaults to lineHeight: 1 + theme fontSize, which shrinks the label vs. Files
                          root: {
                            color: 'inherit',
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            lineHeight: 'inherit',
                          },
                          indicator: {
                            insetInlineStart: 0,
                            insetInlineEnd: 'auto',
                            transform: 'translate(40%, -80%)',
                            transformOrigin: '0% 0%',
                          },
                        }}
                      >
                        <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                          <Icon name="MailIcon" className="h-4 w-4 shrink-0" />
                          <span className="hidden whitespace-nowrap lg:inline">
                            {t('common.emails')}
                          </span>
                          <span className="sr-only">{t('a11y.openEmailsSection')}</span>
                        </div>
                      </Badge>
                    </Link>
                  </div>
                </nav>
              )}
              <MainButtonMenu context={routerPageContext} />
            </>
          }
          fixedFooter={
            <div className="border-gray-medium/60 relative z-10 flex flex-col items-stretch gap-1 border-t px-1 pt-2 pb-2">
              {isIconOnlySider ? (
                <Tooltip title={shortcutsLabel} placement="right">
                  {shortcutsButton}
                </Tooltip>
              ) : (
                shortcutsButton
              )}
              <Divider className="!border-gray-medium/60 !my-1" />
              <div className="flex flex-col items-center gap-0.5 px-0.5">
                <a
                  href="https://www.cybergrant.net/en/cybergrant-release-note-updates-and-new-features"
                  className="!text-gray-dark hover:!text-blue-dark max-w-full text-center text-[10px] break-all underline underline-offset-2 transition-colors lg:text-xs dark:hover:!text-black"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={appVersionLabel}
                >
                  {appVersionLabel}
                </a>
                {__BUILD_ID__ ? (
                  <span
                    className="!text-gray-dark max-w-full text-center text-[10px] break-all"
                    title={__BUILD_ID__}
                  >
                    Build: {buildIdLabel}
                  </span>
                ) : null}
              </div>
            </div>
          }
        >
          <div className="flex flex-1 flex-col gap-6 overflow-x-hidden">
            <ul className="flex flex-col">
              {updatedRouterPages.map((link) => (
                <li
                  key={link.key}
                  className={clsx(
                    'group',
                    link.isDisabled && 'pointer-events-none text-base opacity-50',
                    link.hasDivider && 'border-gray-medium mb-1 border-b',
                  )}
                >
                  <NavLink
                    to={link.href}
                    aria-disabled={link.isDisabled || undefined}
                    tabIndex={link.isDisabled ? -1 : undefined}
                    onClick={() => {
                      // Clear saved setting key when clicking on Settings from sidebar
                      if (link.key === 'settings') {
                        appEvents.settingsScroll.clearLastClickedSetting();
                      }
                    }}
                    viewTransition
                    className="block"
                  >
                    <div
                      className={clsx(
                        'text-blue-dark/70 relative flex flex-row items-center rounded-md px-1 py-2 transition-all duration-400 hover:bg-black/5 lg:gap-2',
                      )}
                    >
                      {link.notificationsCount > 0 && (
                        <div className="bg-red absolute top-0 left-10 flex h-4 w-4 items-center justify-center rounded-full text-center">
                          <span
                            className={clsx(
                              'leading-1 text-white',
                              link.notificationsCount > 99 ? 'text-xss' : 'text-xs',
                            )}
                          >
                            {link.notificationsCount > 99 ? '99+' : link.notificationsCount}
                          </span>
                        </div>
                      )}
                      <div
                        className={clsx(
                          'h-5 w-1 rounded-full transition-all duration-200 ease-in-out lg:mr-2',
                          location.pathname.startsWith(link.href)
                            ? 'bg-blue-dark'
                            : 'group-hover:bg-gray-medium',
                        )}
                      />
                      <Icon name={link.icon} className="m-auto w-10" />
                      <span
                        className={clsx(
                          'my-auto hidden w-full truncate pl-1.5 text-lg lg:block',
                          location.pathname.startsWith(link.href) && 'text-azure',
                        )}
                      >
                        {t(link.label)}
                      </span>
                    </div>
                  </NavLink>
                </li>
              ))}
            </ul>
            {!isEmailMode && <TagsList />}
          </div>
        </Sidebar>
        <main
          id="main-content"
          tabIndex={-1}
          className={clsx(
            // min-w-0 not w-screen: fixed sidebar ml-* + 100vw prevented flex shrink (wide Tabs min-content).
            'relative mt-[var(--app-header-stack-height,3.5rem)] mr-4 ml-[60px] min-w-0 flex-1 shrink overflow-x-hidden py-4 transition-all duration-300 lg:ml-[205px]',
            // Match RightPanel max-w-[480px] (+5px gap; was 355 when panel was 350).
            // Skip margin when viewer drawer hosts the nested panel (no global aside).
            showGlobalRightPanel ? 'xl:mr-[485px]' : 'mr-0',
          )}
        >
          <div className="h-full min-w-0 flex-1">
            <ErrorBoundary>
              <OverlayScrollbarsComponent
                options={{
                  scrollbars: {
                    autoHide: 'leave',
                  },
                }}
                defer
                style={{ height: '100%', overflow: 'auto' }}
              >
                <Suspense
                  fallback={
                    <div className="flex h-full w-full items-center justify-center">
                      <Spin />
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </OverlayScrollbarsComponent>
            </ErrorBoundary>
          </div>
        </main>
        <RightPanel
          isOpen={showGlobalRightPanel}
          onClose={closeRightPanel}
          hasCloseButton={hasRightPanelCloseButton}
          checkDirtyOnClose={isRightPanelCheckDirtyOnClose}
        >
          {rightPanelContent}
        </RightPanel>
        <TransferProgressDrawers />
        <FilegrantViewerDrawer />

        {/* Email mode: FAB + Composer + Send Overlay */}
        {isEmailMode &&
          !hasInterfaceLimitation(
            authenticatedUser?.settings || [],
            InterfaceLimitationEnum.DenyCreateEmail,
          ) && (
            <button
              type="button"
              aria-label={t('a11y.composerFab')}
              className="fixed right-8 bottom-8 z-50 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl active:scale-100"
              style={{
                backgroundColor: brandingData?.mainButtonsColorBackground || '#193057',
                color: brandingData?.mainButtonsColorForeground || '#fff',
              }}
              onClick={openEmailComposer}
            >
              <Icon name="MailPlusIcon" className="h-6 w-6" />
            </button>
          )}
      </div>
    </KeyboardShortcutsProvider>
  );
}

export default MainLayout;
